import { useState, useEffect, useCallback } from "react";

let eventTarget;

try {
	eventTarget = new EventTarget();
} catch {
	eventTarget = document.createElement("use-storage-event-target");
}

const defaultItemOptions = { exp: 24 * 60 * 60 * 1000 };

const getStorageItem = (key, defaultValue) => {
	const raw = localStorage.getItem(key);
	let itemValue = defaultValue;
	if (raw) {
		let item = JSON.parse(raw);
		if (!item || (item.expTime && item.expTime < new Date().getTime())) {
			localStorage.removeItem(key);
			itemValue = defaultValue;
		} else {
			itemValue = item.value;
		}
	}
	return itemValue;
};

const setStorageItem = (key, value, options) => {
	let item = {
		exp: options.exp,
		currTime: new Date().getTime(),
		...options,
		value: value,
	};
	item.expTime = new Date(item.currTime + item.exp).getTime();

	localStorage.setItem(key, JSON.stringify(item));
};

const useStorage = (key, defaultValue, options = defaultItemOptions) => {
	const updater = useCallback(
		(updatedValue, remove = false) => {
			setValue(updatedValue);

			if (remove) {
				localStorage.remove(key);
			} else {
				setStorageItem(key, updatedValue, options);
			}
			eventTarget.dispatchEvent(new CustomEvent("storage_change", { detail: { key } }));
		},
		[key, options]
	);

	const item = getStorageItem(key, defaultValue);
	const [value, setValue] = useState(item);

	defaultValue != null && !item && updater(defaultValue);

	useEffect(() => {
		const listener = ({ detail }) => {
			if (detail.key === key) {
				const lItem = getStorageItem(key, defaultValue);

				lItem !== item && setValue(lItem);
			}
		};

		eventTarget.addEventListener("storage_change", listener);
		return () => eventTarget.removeEventListener("storage_change", listener);
	});

	return [value, updater, () => updater(null, true)];
};

export const useLocalStorage = useStorage;
export const getLocalStorageItem = getStorageItem;
export const setLocalStorageItem = setStorageItem;
